import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Grid,
  Theme,
  CircularProgress,
} from '@mui/material';

import { postData, putData } from '../../utils/apiClient';
import { PERMISSIONS } from '../../constants/permissions';

// User roles
const USER_ROLES = ['ADMIN', 'EDITOR', 'REVIEWER'] as const;

type UserRole = (typeof USER_ROLES)[number];

interface User {
  userId: string;
  name: string;
  email: string;
  role: string;
  createdAt: number;
  updatedAt: number;
}

interface UserFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: any) => void;
  onError?: (message: string) => void; // Callback for error notifications
  user?: User | null; // If provided, dialog is in edit mode
}

interface FormData {
  name: string;
  email: string;
  role: UserRole | '';
}

const UserFormDialog: React.FC<UserFormDialogProps> = ({
  open,
  onClose,
  onSave,
  onError,
  user,
}) => {
  const isEditMode = !!user;

  // Form data state
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    role: '',
  });

  // Loading state
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form validation errors
  const [errors, setErrors] = useState<{
    name?: string;
    email?: string;
    role?: string;
  }>({});

  // Reset form data
  const resetFormData = () => {
    setFormData({
      name: '',
      email: '',
      role: '',
    });
    setErrors({});
    setIsSubmitting(false);
  };

  // Populate form when user data changes (edit mode)
  useEffect(() => {
    if (open) {
      if (user) {
        setFormData({
          name: user.name || '',
          email: user.email || '',
          role: (user.role as UserRole) || '',
        });
      } else {
        resetFormData();
      }
    }
  }, [open, user]);

  // Handle form field changes
  const handleFormChange = (field: keyof FormData, value: string) => {
    const normalizedValue = field === 'email' ? value.toLowerCase() : value;

    setFormData((prev) => ({
      ...prev,
      [field]: normalizedValue,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.role) {
      newErrors.role = 'Role is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle dialog close
  const handleClose = (event: any, reason: string) => {
    // Only allow closing via buttons, not backdrop click or escape key
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      return;
    }
    resetFormData();
    onClose();
  };

  // Handle cancel button
  const handleCancel = () => {
    resetFormData();
    onClose();
  };

  // Handle save
  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    const normalizedEmail = formData.email.trim().toLowerCase();

    try {
      let response;

      if (isEditMode && user) {
        const requestBodyUpdate = {
          name: formData.name.trim(),
          email: normalizedEmail,
          role: formData.role,
          userId: user?.userId || '',
        };
        // Update existing user
        response = await putData(
          `/users`,
          PERMISSIONS.UPDATE_USER,
          requestBodyUpdate
        );
      } else {
        // Create new user
        const requestBodyCreate = {
          users: [
            {
              name: formData.name.trim(),
              email: normalizedEmail,
              role: formData.role,
            },
          ]
        };
        response = await postData(
          '/users',
          PERMISSIONS.CREATE_USER,
          requestBodyCreate
        );
      }

      // Call parent's onSave callback with the response
      onSave(response);

      // Reset and close
      resetFormData();
      onClose();
    } catch (error: any) {
      console.error('Error saving user:', error);

      if (isEditMode === false) {
        if (error.data?.summary?.failed !== 0) {
          const errorReason = 'Internal Server Error';
          const errorMessage = `Failed to create user. Reason: ${errorReason}`;
          if (onError) {
            onError(errorMessage);
          }
        }
        else if(error.data?.summary?.duplicates !== 0){
          if(onError){
            onError("Error creating user. User already exists.");
          }
        }else{
          if(onError){
            onError("Error creating user. Reason: Internal Server Error");
          }
        }
      }
      else{
        if (error.data?.errors?.length > 0) {
          if(onError){
            onError("Error updating user. Reason: " + error.data?.errors[0]?.message);
          }
        }
        else if(error.data?.summary?.duplicates !== 0){
          if(onError){
            onError("Error updating user. User already exists.");
          }
        }
        else{
          if(onError){
            onError("Error updating user. Reason: Internal Server Error");
          }
        }
      }

      // Reset and close dialog
      resetFormData();
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 2,
          backgroundColor: 'white',
        },
      }}
    >
      <DialogTitle sx={{ marginY: 1 }}>
        <Typography variant="h5">
          {isEditMode ? 'Edit User' : 'Add New User'}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
          <Grid container spacing={2}>
            {/* Name Field */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name *"
                variant="filled"
                value={formData.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
                error={!!errors.name}
                helperText={errors.name}
                disabled={isSubmitting}
                placeholder="Enter user's full name"
              />
            </Grid>

            {/* Email Field */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email *"
                variant="filled"
                type="email"
                value={formData.email}
                onChange={(e) => handleFormChange('email', e.target.value)}
                error={!!errors.email}
                helperText={errors.email}
                disabled={isSubmitting || isEditMode}
                placeholder="Enter user's email address"
              />
            </Grid>

            {/* Role Field */}
            <Grid item xs={12}>
              <FormControl fullWidth error={!!errors.role}>
                <InputLabel>Role *</InputLabel>
                <Select
                  value={formData.role}
                  variant="filled"
                  label="Role *"
                  onChange={(e) => handleFormChange('role', e.target.value)}
                  disabled={isSubmitting}
                >
                  {USER_ROLES.map((role) => (
                    <MenuItem key={role} value={role}>
                      <Typography variant="body2">{role}</Typography>
                    </MenuItem>
                  ))}
                </Select>
                {errors.role && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, ml: 1.5 }}
                  >
                    {errors.role}
                  </Typography>
                )}
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button
          onClick={handleCancel}
          disabled={isSubmitting}
          sx={(theme: Theme) => ({
            color: theme.palette.grey[600],
            textTransform: 'none',
            fontWeight: 'normal',
          })}
        >
          CANCEL
        </Button>
        <Button
          onClick={handleSave}
          disabled={isSubmitting}
          variant="contained"
          sx={(theme: Theme) => ({
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            textTransform: 'none',
            fontWeight: 'normal',
            '&:hover': {
              backgroundColor: theme.palette.primary.dark,
            },
          })}
          startIcon={
            isSubmitting ? <CircularProgress size={20} color="inherit" /> : null
          }
        >
          {isSubmitting ? 'SAVING...' : 'SAVE'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserFormDialog;
